import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';

import { fg } from '@confluence/feature-gating';

// eslint-disable-next-line import/no-cycle
import { BulkSpacePermissionsTable } from '../BulkSpacePermissionsTable.tsx';
import type { SpacePermissionType } from '../graphql/__types__/BulkSpacePermissionsMutation.ts';

const i18n = defineMessages({
	permissionsSelectionHeader: {
		id: 'bulk-permissions.space.permissions-selection-header',
		defaultMessage: 'Choose space permissions',
		description: 'Header text of the permissions selection section',
	},
	permissionsSelectionSubtext: {
		id: 'bulk-permissions.space.permissions-selection-subtext',
		defaultMessage:
			'Select which permissions the user or group should have in all spaces. This will overwrite any existing permissions they have. <b>This can’t be undone</b> so make sure to double check who you’re adding and what permissions they should have.',
		description: 'Subtext of the permissions selection section',
	},
	permissionsWithTeamsSelectionSubtext: {
		id: 'bulk-permissions.space.permissions-selection-subtext-with-teams',
		defaultMessage:
			'Select which permissions the user, team or group should have in all spaces. This will overwrite any existing permissions they have. <b>This can’t be undone</b> so make sure to double check who you’re adding and what permissions they should have.',
		description: 'Subtext of the permissions selection section',
	},
	permissionsSelectionSubtextAdditional: {
		id: 'bulk-permissions.space.permissions-selection-subtext-additional',
		defaultMessage: 'To remove permissions from all spaces, uncheck the view permission.',
		description: 'Additional subtext of the permissions selection section',
	},
});

const permissionSelectionHeaderStyle = xcss({
	font: 'font.body.large',
	fontWeight: 'font.weight.medium',
	paddingBottom: 'space.100',
	paddingTop: 'space.300',
});

const subtextStyle = xcss({
	font: 'font.body',
});

type PermissionSelectionSectionProps = {
	spacePermissions: SpacePermissionType[];
	setSpacePermissions: (permissions: SpacePermissionType[]) => void;
	isDisabled?: boolean;
};

export const PermissionSelectionSection = ({
	spacePermissions,
	setSpacePermissions,
	isDisabled = false,
}: PermissionSelectionSectionProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<>
			<Box xcss={permissionSelectionHeaderStyle}>
				{formatMessage(i18n.permissionsSelectionHeader)}
			</Box>
			<Box xcss={subtextStyle}>
				{formatMessage(
					fg('confluence_frontend_perms_exp_teams_as_principals')
						? i18n.permissionsWithTeamsSelectionSubtext
						: i18n.permissionsSelectionSubtext,
					{
						b: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
					},
				)}
				<br />
				<Box paddingBlockStart="space.200">
					{formatMessage(i18n.permissionsSelectionSubtextAdditional)}
				</Box>
			</Box>
			<BulkSpacePermissionsTable
				spacePermissions={spacePermissions}
				setSpacePermissions={setSpacePermissions}
				isDisabled={isDisabled}
			/>
		</>
	);
};
